// Bilingual EN/RO content for CDPA SYSTEMS CONCEPT SRL
export const translations = {
  en: {
    nav: {
      home: 'Home',
      capabilities: 'Capabilities',
      research: 'Research & Innovation',
      partnerships: 'Partnerships',
      contact: 'Contact',
    },
    cta: {
      learnMore: 'Learn More',
      contactUs: 'Contact Us',
      sendInquiry: 'Send Inquiry',
      sending: 'Sending...',
    },
    hero: {
      overline: 'Romanian Defense Technology SME',
      headline: 'Advanced Defense Technologies for a Safer Future',
      subheadline:
        'Research, Development and Innovation in Ballistic Protection, Ground Vehicle Mobility & Operational Support, CBRNE Technologies, Ammunition Systems and Counter-UAS Solutions.',
      body:
        'CDPA SYSTEMS CONCEPT SRL is a Romanian SME specialized in defense and security research, engineering, technology development and innovation activities supporting military capabilities, operational readiness and critical infrastructure protection.',
      stat1: { value: '6', label: 'Capability Domains' },
      stat2: { value: 'NATO', label: 'Oriented Standards' },
      stat3: { value: 'EU', label: 'Cooperation Programs' },
    },
    who: {
      overline: 'Who We Are',
      title: 'Multidisciplinary defense engineering, from concept to capability.',
      body:
        'CDPA SYSTEMS CONCEPT SRL develops advanced defense and security solutions through multidisciplinary research, engineering and innovation activities. The company supports national and international programs focused on military modernization, force protection, operational effectiveness and emerging security challenges.',
      highlights: [
        'Research & Development',
        'Engineering Excellence',
        'Defense Innovation',
        'European Cooperation',
        'NATO-Oriented Standards',
        'Technology Integration',
      ],
    },
    capabilitiesIntro: {
      overline: 'Capabilities',
      title: 'Six mission-critical domains.',
      body:
        'Layered, integrated solutions designed for survivability, mobility, protection, energy and decision advantage across the modern operational continuum.',
    },
    ballistic: {
      overline: '01 / Capability',
      title: 'Ballistic Protection & Survivability Solutions',
      body:
        'CDPA SYSTEMS CONCEPT SRL possesses expertise in ballistic protection systems for armored vehicles and military platforms. The company develops lightweight and modular armor solutions designed to improve survivability against kinetic energy threats, blast effects and fragmentation while maintaining mobility and operational performance.',
      cards: [
        { title: 'Lightweight Armor Solutions', desc: 'Composite & ceramic systems engineered for weight-to-protection optimization.' },
        { title: 'Modular Protection Systems', desc: 'Field-replaceable panels adapted to mission-specific threat profiles.' },
        { title: 'Blast Resistance Research', desc: 'IED & mine blast mitigation through structural and energy-absorbing design.' },
        { title: 'Vehicle Survivability Enhancement', desc: 'Integrated survivability for legacy and next-generation platforms.' },
        { title: 'Advanced Materials Engineering', desc: 'High-performance composites and metallurgical research for armor.' },
        { title: 'Military Platform Protection', desc: 'End-to-end protection engineering across wheeled and tracked platforms.' },
      ],
    },
    mobility: {
      overline: '02 / Capability',
      title: 'Ground Vehicle Mobility & Operational Support',
      body:
        'CDPA SYSTEMS CONCEPT SRL develops and integrates operational support solutions for ground vehicles, with a focus on mobility, preventive maintenance and mission readiness. These solutions are designed to reduce downtime, increase fleet reliability and support military, emergency and industrial vehicles operating in demanding environments.',
      body2:
        'The company\u2019s activities include research, testing and integration of tire puncture prevention solutions, mobility support systems, specialized technical fluids, advanced coolants, special antifreeze products and maintenance technologies adapted for harsh operational conditions.',
      cards: [
        { title: 'Tire Puncture Prevention', desc: 'Self-sealing technologies maintaining mobility under hostile conditions.' },
        { title: 'Run-Flat & Mobility Support', desc: 'Sustained mobility after ballistic or terrain damage.' },
        { title: 'Specialized Technical Fluids', desc: 'Mission-grade lubricants and hydraulic media for extreme service.' },
        { title: 'Advanced Antifreeze & Coolants', desc: 'Temperature-resilient formulations for high-stress engines.' },
        { title: 'Fleet Maintenance Support', desc: 'Predictive maintenance frameworks reducing downtime.' },
        { title: 'Mission Readiness', desc: 'Operational availability optimization across fleet lifecycles.' },
        { title: 'Harsh Environment Performance', desc: 'Solutions qualified for arctic, desert and mountainous terrain.' },
        { title: 'Operational Reliability', desc: 'Reliability engineering across mechanical and chemical subsystems.' },
      ],
    },
    cbrne: {
      overline: '03 / Capability',
      title: 'CBRNE Defense Technologies',
      body:
        'CDPA SYSTEMS CONCEPT SRL is involved in the development of detection, monitoring, protection and response solutions intended to improve resilience against Chemical, Biological, Radiological, Nuclear and Explosive threats.',
      body2:
        'Research activities include sensor integration, threat assessment methodologies, protective systems and decision-support tools for emergency response and crisis management.',
      cards: [
        { title: 'Threat Detection Systems', desc: 'Multi-modal sensors for chemical and radiological signatures.' },
        { title: 'Sensor Integration', desc: 'Networked sensor architectures for area surveillance.' },
        { title: 'Protective Technologies', desc: 'Personal and collective protection for first responders.' },
        { title: 'Emergency Response Support', desc: 'Command tools for crisis coordination and triage.' },
        { title: 'Critical Infrastructure Protection', desc: 'Resilience programs for energy, transport and utilities.' },
        { title: 'Threat Assessment Methodologies', desc: 'Risk-based methodologies aligned with international standards.' },
        { title: 'Decision-Support Tools', desc: 'Software for situational awareness and response planning.' },
      ],
    },
    ammo: {
      overline: '04 / Capability',
      title: 'Ammunition and Weapon System Engineering',
      body:
        'In the field of munitions and weapon systems, CDPA SYSTEMS CONCEPT SRL performs research and engineering activities related to ammunition technologies, safety and qualification processes, performance assessment, interoperability requirements and compliance with NATO standards.',
      body2:
        'The company contributes to the development of innovative solutions aimed at increasing operational effectiveness, safety and reliability across a wide range of defense applications.',
      cards: [
        { title: 'Safety & Qualification', desc: 'Test campaigns aligned with STANAG and national standards.' },
        { title: 'Performance Evaluation', desc: 'Ballistic and terminal effects assessment in controlled conditions.' },
        { title: 'Interoperability Assessment', desc: 'Cross-platform qualification for allied force structures.' },
        { title: 'NATO Compliance', desc: 'End-to-end alignment with NATO ammunition standards.' },
        { title: 'Technology Development', desc: 'Innovation in propellants, fuzing and projectile design.' },
        { title: 'Reliability Assessment', desc: 'Statistical reliability and shelf-life qualification.' },
        { title: 'Defense Application Support', desc: 'Engineering support across the ammunition lifecycle.' },
      ],
    },
    cuas: {
      overline: '05 / Capability',
      title: 'Counter-UAS and Anti-Drone Technologies',
      body:
        'CDPA SYSTEMS CONCEPT SRL is actively engaged in the rapidly evolving domain of Counter-Unmanned Aircraft Systems and anti-drone technologies. The company develops and integrates detection, tracking, identification and neutralization capabilities against unmanned aerial threats.',
      body2:
        'Research efforts focus on multi-sensor architectures, artificial intelligence-based threat recognition, electronic warfare applications and layered protection concepts designed to counter individual drones as well as coordinated drone swarms.',
      cards: [
        { title: 'Drone Detection', desc: 'RF, radar and acoustic sensing for early warning.' },
        { title: 'Threat Tracking', desc: 'Continuous track maintenance across heterogeneous sensors.' },
        { title: 'Threat Identification', desc: 'Friend-or-foe and class-level UAV identification.' },
        { title: 'Artificial Intelligence Integration', desc: 'ML models for behavior, intent and swarm analysis.' },
        { title: 'Electronic Warfare Applications', desc: 'Layered EW responses against C2 and navigation links.' },
        { title: 'Layered Protection Architectures', desc: 'Hard-kill and soft-kill orchestration frameworks.' },
        { title: 'Drone Swarm Countermeasures', desc: 'Distributed engagement against coordinated UAV swarms.' },
      ],
    },
    battery: {
      overline: '06 / Capability',
      title: 'Next-Generation Battery Technologies',
      body:
        'CDPA SYSTEMS CONCEPT SRL contributes its technological and application-oriented expertise to collaborative research on advanced battery and energy storage systems. Acting as the industrial bridge between laboratory research and real-world deployment, the company supports the development of safer, higher-performance rechargeable energy solutions for defense and dual-use applications.',
      body2:
        'The company\u2019s contribution spans early-stage technological requirements, fabrication and scalability assessment, cell-level integration and validation, and technology transfer, ensuring that emerging battery innovations are reproducible, manufacturable and aligned with market and operational needs.',
      cards: [
        { title: 'Advanced Energy Storage R&D', desc: 'Collaborative research into next-generation rechargeable battery systems.' },
        { title: 'Materials & Process Evaluation', desc: 'Application-oriented feedback on new materials and fabrication processes.' },
        { title: 'Performance & Cycling Validation', desc: 'Testing of charge-discharge behavior, rate capability and long-term stability.' },
        { title: 'Manufacturing Scalability', desc: 'Assessing reproducibility and industrial scale-up of new cell architectures.' },
        { title: 'Technology Transfer', desc: 'Bridging laboratory breakthroughs and market-ready energy solutions.' },
        { title: 'European Research Collaboration', desc: 'Joint innovation with universities and research institutes across the EU.' },
      ],
    },
    research: {
      overline: 'Research & Innovation',
      title: 'Research for Next-Generation Defense Technologies',
      body:
        'Through its multidisciplinary expertise, CDPA SYSTEMS CONCEPT SRL supports national and international defense programs, collaborative research initiatives and industrial partnerships aimed at strengthening European technological sovereignty, enhancing defense readiness and addressing emerging security challenges.',
      body2:
        'The company actively participates in innovation ecosystems involving industry, research organizations, universities and governmental stakeholders, contributing to the development of next-generation defense technologies and dual-use solutions.',
      pillars: [
        'Defense Research',
        'Technology Innovation',
        'Industrial Collaboration',
        'Dual-Use Technologies',
        'European Technological Sovereignty',
        'Operational Readiness',
      ],
    },
    partnerships: {
      overline: 'Partnerships',
      title: 'Collaboration & Strategic Partnerships',
      body:
        'CDPA SYSTEMS CONCEPT SRL works with defense industry partners, research organizations, universities and governmental stakeholders to accelerate innovation and develop advanced security solutions.',
      body2:
        'The company supports collaborative research initiatives and industrial partnerships aimed at strengthening defense readiness, technological autonomy and European security capabilities.',
      partners: [
        'Industry',
        'Research Institutes',
        'Universities',
        'Government Organizations',
        'European Programs',
        'Defense Innovation Networks',
      ],
    },
    contact: {
      overline: 'Contact',
      title: 'Contact CDPA SYSTEMS CONCEPT',
      body:
        'Interested in collaboration opportunities, research partnerships, industrial cooperation or defense technology projects? Contact our team.',
      labels: {
        name: 'Name',
        company: 'Company',
        email: 'Email',
        phone: 'Phone',
        message: 'Message',
      },
      placeholders: {
        name: 'Your full name',
        company: 'Your organization',
        email: 'name@organization.eu',
        phone: '+40 ...',
        message: 'Briefly describe your inquiry, partnership scope or project context.',
      },
      success: 'Inquiry received. Our team will contact you shortly.',
      error: 'Submission failed. Please verify the fields and try again.',
    },
    footer: {
      tagline: 'Research \u2022 Development \u2022 Innovation \u2022 Defense Technologies',
      country: 'Romania',
      rights: 'All rights reserved.',
      menu: 'Navigate',
      legal: 'Legal',
      privacy: 'Privacy Notice',
      terms: 'Terms of Use',
    },
  },
  ro: {
    nav: {
      home: 'Acas\u0103',
      capabilities: 'Capabilit\u0103\u021bi',
      research: 'Cercetare & Inovare',
      partnerships: 'Parteneriate',
      contact: 'Contact',
    },
    cta: {
      learnMore: 'Afl\u0103 mai mult',
      contactUs: 'Contacteaz\u0103-ne',
      sendInquiry: 'Trimite mesaj',
      sending: 'Se trimite...',
    },
    hero: {
      overline: 'IMM rom\u00e2nesc de tehnologie pentru ap\u0103rare',
      headline: 'Tehnologii avansate de ap\u0103rare pentru un viitor mai sigur',
      subheadline:
        'Cercetare, dezvoltare \u0219i inovare \u00een protec\u021bie balistic\u0103, mobilitate \u0219i suport opera\u021bional pentru vehicule terestre, tehnologii CBRNE, sisteme de muni\u021bie \u0219i solu\u021bii Counter-UAS.',
      body:
        'CDPA SYSTEMS CONCEPT SRL este un IMM rom\u00e2nesc specializat \u00een cercetare, inginerie, dezvoltare tehnologic\u0103 \u0219i inovare \u00een domeniul ap\u0103r\u0103rii \u0219i securit\u0103\u021bii, dedicat consolid\u0103rii capabilit\u0103\u021bilor militare, preg\u0103tirii opera\u021bionale \u0219i protec\u021biei infrastructurilor critice.',
      stat1: { value: '6', label: 'Domenii de capabilitate' },
      stat2: { value: 'NATO', label: 'Standarde aliniate' },
      stat3: { value: 'UE', label: 'Programe de cooperare' },
    },
    who: {
      overline: 'Cine suntem',
      title: 'Inginerie multidisciplinar\u0103 de ap\u0103rare, de la concept la capabilitate.',
      body:
        'CDPA SYSTEMS CONCEPT SRL dezvolt\u0103 solu\u021bii avansate de ap\u0103rare \u0219i securitate prin activit\u0103\u021bi multidisciplinare de cercetare, inginerie \u0219i inovare. Compania sus\u021bine programe na\u021bionale \u0219i interna\u021bionale dedicate moderniz\u0103rii militare, protec\u021biei for\u021belor, eficien\u021bei opera\u021bionale \u0219i provoc\u0103rilor emergente de securitate.',
      highlights: [
        'Cercetare \u0219i dezvoltare',
        'Excelen\u021b\u0103 \u00een inginerie',
        'Inovare \u00een ap\u0103rare',
        'Cooperare european\u0103',
        'Standarde NATO',
        'Integrare tehnologic\u0103',
      ],
    },
    capabilitiesIntro: {
      overline: 'Capabilit\u0103\u021bi',
      title: '\u0218ase domenii critice misiunii.',
      body:
        'Solu\u021bii integrate \u0219i stratificate pentru supravie\u021buire, mobilitate, protec\u021bie, energie \u0219i avantaj decizional \u00een spa\u021biul opera\u021bional modern.',
    },
    ballistic: {
      overline: '01 / Capabilitate',
      title: 'Protec\u021bie balistic\u0103 \u0219i supravie\u021buire',
      body:
        'CDPA SYSTEMS CONCEPT SRL de\u021bine expertiz\u0103 \u00een sisteme de protec\u021bie balistic\u0103 pentru vehicule blindate \u0219i platforme militare. Dezvolt\u0103m solu\u021bii de armur\u0103 u\u0219oar\u0103 \u0219i modular\u0103 menite s\u0103 \u00eembun\u0103t\u0103\u021beasc\u0103 supravie\u021buirea \u00eempotriva amenin\u021b\u0103rilor cinetice, suflului \u0219i fragment\u0103rii, p\u0103str\u00e2nd mobilitatea \u0219i performan\u021ba opera\u021bional\u0103.',
      cards: [
        { title: 'Armur\u0103 u\u0219oar\u0103', desc: 'Sisteme compozite \u0219i ceramice optimizate greutate-protec\u021bie.' },
        { title: 'Protec\u021bie modular\u0103', desc: 'Panouri \u00eenlocuibile adaptate profilelor de amenin\u021bare.' },
        { title: 'Rezisten\u021b\u0103 la suflu', desc: 'Reducerea efectelor IED \u0219i mine prin design structural.' },
        { title: 'Cre\u0219terea supravie\u021buirii', desc: 'Supravie\u021buire integrat\u0103 pentru platforme existente \u0219i noi.' },
        { title: 'Materiale avansate', desc: 'Cercetare \u00een compozite \u0219i metalurgie de \u00eenalt\u0103 performan\u021b\u0103.' },
        { title: 'Protec\u021bia platformelor', desc: 'Inginerie complet\u0103 pentru platforme cu ro\u021bi \u0219i \u0219enile.' },
      ],
    },
    mobility: {
      overline: '02 / Capabilitate',
      title: 'Mobilitate vehicule terestre \u0219i suport opera\u021bional',
      body:
        'CDPA SYSTEMS CONCEPT SRL dezvolt\u0103 \u0219i integreaz\u0103 solu\u021bii de suport opera\u021bional pentru vehicule terestre, cu accent pe mobilitate, mentenan\u021b\u0103 preventiv\u0103 \u0219i preg\u0103tire pentru misiune. Reducem timpul de indisponibilitate, cre\u0219tem fiabilitatea flotelor \u0219i sus\u021binem vehicule militare, de urgen\u021b\u0103 \u0219i industriale \u00een medii exigente.',
      body2:
        'Activit\u0103\u021bile includ cercetare, testare \u0219i integrare de solu\u021bii anti-pan\u0103, sisteme de mobilitate, fluide tehnice specializate, lichide de r\u0103cire avansate, antigeluri speciale \u0219i tehnologii de mentenan\u021b\u0103 adaptate condi\u021biilor opera\u021bionale dure.',
      cards: [
        { title: 'Prevenire pan\u0103 anvelope', desc: 'Tehnologii autoetan\u0219ante p\u0103str\u00e2nd mobilitatea \u00een condi\u021bii ostile.' },
        { title: 'Run-Flat \u0219i suport mobilitate', desc: 'Mobilitate sus\u021binut\u0103 dup\u0103 deteriorare balistic\u0103 sau de teren.' },
        { title: 'Fluide tehnice specializate', desc: 'Lubrifian\u021bi \u0219i medii hidraulice de calitate misiune.' },
        { title: 'Antigeluri \u0219i lichide de r\u0103cire', desc: 'Formul\u0103ri rezistente termic pentru motoare solicitate.' },
        { title: 'Suport mentenan\u021b\u0103 flot\u0103', desc: 'Cadre de mentenan\u021b\u0103 predictiv\u0103 pentru disponibilitate maxim\u0103.' },
        { title: 'Preg\u0103tire pentru misiune', desc: 'Optimizarea disponibilit\u0103\u021bii opera\u021bionale a flotei.' },
        { title: 'Performan\u021b\u0103 \u00een medii dure', desc: 'Solu\u021bii calificate pentru teren arctic, deser\u021b \u0219i munte.' },
        { title: 'Fiabilitate opera\u021bional\u0103', desc: 'Inginerie de fiabilitate pentru subsisteme mecanice \u0219i chimice.' },
      ],
    },
    cbrne: {
      overline: '03 / Capabilitate',
      title: 'Tehnologii de ap\u0103rare CBRNE',
      body:
        'CDPA SYSTEMS CONCEPT SRL este implicat\u0103 \u00een dezvoltarea solu\u021biilor de detec\u021bie, monitorizare, protec\u021bie \u0219i r\u0103spuns pentru cre\u0219terea rezilien\u021bei \u00eempotriva amenin\u021b\u0103rilor Chimice, Biologice, Radiologice, Nucleare \u0219i Explozive.',
      body2:
        'Activit\u0103\u021bile de cercetare includ integrarea senzorilor, metodologii de evaluare a amenin\u021b\u0103rilor, sisteme de protec\u021bie \u0219i instrumente de sprijin decizional pentru r\u0103spuns \u0219i management de criz\u0103.',
      cards: [
        { title: 'Sisteme de detec\u021bie', desc: 'Senzori multi-modali pentru semn\u0103turi chimice \u0219i radiologice.' },
        { title: 'Integrarea senzorilor', desc: 'Arhitecturi de senzori \u00een re\u021bea pentru supraveghere zonal\u0103.' },
        { title: 'Tehnologii de protec\u021bie', desc: 'Protec\u021bie individual\u0103 \u0219i colectiv\u0103 pentru interven\u021bie.' },
        { title: 'Sprijin pentru urgen\u021be', desc: 'Instrumente de comand\u0103 pentru coordonare \u0219i triaj.' },
        { title: 'Protec\u021bia infrastructurii critice', desc: 'Programe de rezilien\u021b\u0103 pentru energie, transport, utilit\u0103\u021bi.' },
        { title: 'Metodologii de evaluare', desc: 'Metodologii bazate pe risc, aliniate standardelor.' },
        { title: 'Instrumente de sprijin decizional', desc: 'Software pentru con\u0219tientizare situa\u021bional\u0103 \u0219i planificare.' },
      ],
    },
    ammo: {
      overline: '04 / Capabilitate',
      title: 'Inginerie muni\u021bii \u0219i sisteme de armament',
      body:
        '\u00cen domeniul muni\u021biilor \u0219i sistemelor de armament, CDPA SYSTEMS CONCEPT SRL desf\u0103\u0219oar\u0103 activit\u0103\u021bi de cercetare \u0219i inginerie privind tehnologiile de muni\u021bii, procesele de siguran\u021b\u0103 \u0219i calificare, evaluarea performan\u021bei, interoperabilitatea \u0219i conformitatea cu standardele NATO.',
      body2:
        'Compania contribuie la dezvoltarea de solu\u021bii inovatoare pentru cre\u0219terea eficien\u021bei opera\u021bionale, a siguran\u021bei \u0219i a fiabilit\u0103\u021bii \u00een aplica\u021bii diverse de ap\u0103rare.',
      cards: [
        { title: 'Siguran\u021b\u0103 \u0219i calificare', desc: 'Campanii de testare aliniate STANAG \u0219i standardelor na\u021bionale.' },
        { title: 'Evaluare performan\u021b\u0103', desc: 'Evaluare balistic\u0103 \u0219i efecte terminale \u00een condi\u021bii controlate.' },
        { title: 'Interoperabilitate', desc: 'Calificare cross-platform pentru for\u021be aliate.' },
        { title: 'Conformitate NATO', desc: 'Aliniere complet\u0103 cu standardele NATO de muni\u021bie.' },
        { title: 'Dezvoltare tehnologic\u0103', desc: 'Inovare \u00een propelan\u021bi, focoase \u0219i design proiectile.' },
        { title: 'Evaluare fiabilitate', desc: 'Fiabilitate statistic\u0103 \u0219i calificare durat\u0103 stocare.' },
        { title: 'Suport aplica\u021bii ap\u0103rare', desc: 'Sprijin ingineresc pe \u00eentreg ciclul de via\u021b\u0103.' },
      ],
    },
    cuas: {
      overline: '05 / Capabilitate',
      title: 'Tehnologii Counter-UAS \u0219i anti-dron\u0103',
      body:
        'CDPA SYSTEMS CONCEPT SRL este activ\u0103 \u00een domeniul \u00een rapid\u0103 evolu\u021bie al sistemelor Counter-UAS \u0219i tehnologiilor anti-dron\u0103. Dezvolt\u0103m \u0219i integr\u0103m capabilit\u0103\u021bi de detec\u021bie, urm\u0103rire, identificare \u0219i neutralizare \u00eempotriva amenin\u021b\u0103rilor aeriene f\u0103r\u0103 pilot.',
      body2:
        'Eforturile de cercetare se concentreaz\u0103 pe arhitecturi multi-senzor, recunoa\u0219tere bazat\u0103 pe inteligen\u021b\u0103 artificial\u0103, aplica\u021bii de r\u0103zboi electronic \u0219i concepte de protec\u021bie stratificat\u0103 \u00eempotriva dronelor individuale \u0219i a roiurilor.',
      cards: [
        { title: 'Detec\u021bie drone', desc: 'Senzori RF, radar \u0219i acustici pentru avertizare timpurie.' },
        { title: 'Urm\u0103rire amenin\u021b\u0103ri', desc: 'P\u0103strarea continu\u0103 a urm\u0103ririi pe senzori multipli.' },
        { title: 'Identificare amenin\u021b\u0103ri', desc: 'Identificare IFF \u0219i la nivel de clas\u0103 a UAV.' },
        { title: 'Integrare AI', desc: 'Modele ML pentru comportament, inten\u021bie \u0219i roiuri.' },
        { title: 'R\u0103zboi electronic', desc: 'R\u0103spunsuri EW stratificate pe leg\u0103turi C2 \u0219i navigare.' },
        { title: 'Protec\u021bie stratificat\u0103', desc: 'Orchestrare hard-kill \u0219i soft-kill integrat\u0103.' },
        { title: 'Contramasuri roi drone', desc: 'Angajament distribuit \u00eempotriva roiurilor UAV.' },
      ],
    },
    battery: {
      overline: '06 / Capabilitate',
      title: 'Tehnologii de baterii de nou\u0103 genera\u021bie',
      body:
        'CDPA SYSTEMS CONCEPT SRL contribuie cu expertiza sa tehnologic\u0103 \u0219i orientat\u0103 spre aplica\u021bii \u00een cercetarea colaborativ\u0103 privind sisteme avansate de baterii \u0219i stocare a energiei. Ac\u021bion\u00e2nd ca punte industrial\u0103 \u00eentre cercetarea de laborator \u0219i implementarea real\u0103, compania sus\u021bine dezvoltarea unor solu\u021bii de energie reciclabile, mai sigure \u0219i cu performan\u021b\u0103 superioar\u0103, pentru aplica\u021bii de ap\u0103rare \u0219i dual-use.',
      body2:
        'Contribu\u021bia companiei acoper\u0103 definirea cerin\u021belor tehnologice \u00een fazele incipiente, evaluarea fabrica\u021biei \u0219i a scalabilit\u0103\u021bii, integrarea \u0219i validarea la nivel de celul\u0103, precum \u0219i transferul tehnologic, asigur\u00e2nd c\u0103 inova\u021biile emergente \u00een domeniul bateriilor sunt reproductibile, fabricabile \u0219i aliniate cerin\u021belor de pia\u021b\u0103 \u0219i opera\u021bionale.',
      cards: [
        { title: 'Cercetare stocare energie', desc: 'Cercetare colaborativ\u0103 \u00een sisteme de baterii reincarcabile de nou\u0103 genera\u021bie.' },
        { title: 'Evaluare materiale \u0219i procese', desc: 'Feedback orientat spre aplica\u021bii pentru materiale \u0219i procese noi de fabrica\u021bie.' },
        { title: 'Validare performan\u021b\u0103 \u0219i ciclare', desc: 'Testarea comportamentului de \u00eencarcare-desc\u0103rcare \u0219i stabilit\u0103\u021bii pe termen lung.' },
        { title: 'Scalabilitate industrial\u0103', desc: 'Evaluarea reproductibilit\u0103\u021bii \u0219i a extinderii industriale a noilor arhitecturi de celule.' },
        { title: 'Transfer tehnologic', desc: 'Leg\u0103tura \u00eentre descoperirile de laborator \u0219i solu\u021biile de energie pentru pia\u021b\u0103.' },
        { title: 'Colaborare european\u0103 \u00een cercetare', desc: 'Inovare comun\u0103 cu universit\u0103\u021bi \u0219i institute de cercetare din UE.' },
      ],
    },
    research: {
      overline: 'Cercetare & Inovare',
      title: 'Cercetare pentru tehnologii de ap\u0103rare de nou\u0103 genera\u021bie',
      body:
        'Prin expertiza sa multidisciplinar\u0103, CDPA SYSTEMS CONCEPT SRL sus\u021bine programe de ap\u0103rare na\u021bionale \u0219i interna\u021bionale, ini\u021biative de cercetare colaborativ\u0103 \u0219i parteneriate industriale pentru consolidarea suveranit\u0103\u021bii tehnologice europene, preg\u0103tirea pentru ap\u0103rare \u0219i provoc\u0103rile emergente de securitate.',
      body2:
        'Compania particip\u0103 activ \u00een ecosisteme de inovare ce reunesc industria, organiza\u021biile de cercetare, universit\u0103\u021bile \u0219i autorit\u0103\u021bile publice, contribuind la dezvoltarea tehnologiilor de ap\u0103rare de nou\u0103 genera\u021bie \u0219i a solu\u021biilor dual-use.',
      pillars: [
        'Cercetare \u00een ap\u0103rare',
        'Inovare tehnologic\u0103',
        'Colaborare industrial\u0103',
        'Tehnologii dual-use',
        'Suveranitate tehnologic\u0103 european\u0103',
        'Preg\u0103tire opera\u021bional\u0103',
      ],
    },
    partnerships: {
      overline: 'Parteneriate',
      title: 'Colaborare \u0219i parteneriate strategice',
      body:
        'CDPA SYSTEMS CONCEPT SRL colaboreaz\u0103 cu parteneri din industria de ap\u0103rare, organiza\u021bii de cercetare, universit\u0103\u021bi \u0219i autorit\u0103\u021bi publice pentru a accelera inovarea \u0219i a dezvolta solu\u021bii avansate de securitate.',
      body2:
        'Sus\u021binem ini\u021biative de cercetare colaborativ\u0103 \u0219i parteneriate industriale pentru consolidarea preg\u0103tirii pentru ap\u0103rare, a autonomiei tehnologice \u0219i a capabilit\u0103\u021bilor de securitate europene.',
      partners: [
        'Industrie',
        'Institute de cercetare',
        'Universit\u0103\u021bi',
        'Organiza\u021bii guvernamentale',
        'Programe europene',
        'Re\u021bele de inovare \u00een ap\u0103rare',
      ],
    },
    contact: {
      overline: 'Contact',
      title: 'Contactea\u021b\u0103 CDPA SYSTEMS CONCEPT',
      body:
        'Interesat de oportunit\u0103\u021bi de colaborare, parteneriate de cercetare, cooperare industrial\u0103 sau proiecte de tehnologie pentru ap\u0103rare? Contacteaz\u0103 echipa noastr\u0103.',
      labels: {
        name: 'Nume',
        company: 'Companie',
        email: 'Email',
        phone: 'Telefon',
        message: 'Mesaj',
      },
      placeholders: {
        name: 'Nume complet',
        company: 'Organiza\u021bia ta',
        email: 'nume@organizatie.eu',
        phone: '+40 ...',
        message: 'Descrie pe scurt solicitarea, parteneriatul sau proiectul.',
      },
      success: 'Mesajul a fost primit. Echipa noastr\u0103 te va contacta \u00een cur\u00e2nd.',
      error: 'Trimiterea a e\u0219uat. Verific\u0103 c\u00e2mpurile \u0219i \u00eencearc\u0103 din nou.',
    },
    footer: {
      tagline: 'Cercetare \u2022 Dezvoltare \u2022 Inovare \u2022 Tehnologii de ap\u0103rare',
      country: 'Rom\u00e2nia',
      rights: 'Toate drepturile rezervate.',
      menu: 'Naviga\u021bie',
      legal: 'Legal',
      privacy: 'Politica de confiden\u021bialitate',
      terms: 'Termeni de utilizare',
    },
  },
};
